/**
 * Shift agenda — het tabblad van Chatters, functioneel gelijk aan oud.
 *
 * Wat erin zit, in volgorde van belang:
 *   a) Message or block a chatter   (/api/admin/chatter-notices/*, alleen beheer)
 *   b) Shift toevoegen, bewerken, verwijderen; Remove from schedule; reeksen
 *   c) De weekkalender, maandag t/m zondag in agency-tijd
 *   d) De berichtgeschiedenis met leesbevestiging
 *   e) De Telegram-waarschuwingen van deze instantie
 *
 * Tijd: de backend rekent start_at in UTC en de weekdagen (0 = maandag, zoals
 * Python weekday()) in agency-tijd, Europe/Amsterdam. Invoer en weergave gaan
 * hier daarom bewust in Amsterdam en NIET in de voorkeurszone van de
 * gebruiker: een chatter in Manila die "Mon" aanvinkt, bedoelt de maandag van
 * het bureau, en zo telt de server hem ook.
 *
 * Rechten volgen de server (admin_chatter_finance.py `_shift_scope`,
 * admin_chatter_notices.py `_assert_may_act_on`):
 *   - diensten zien:       iedereen met een scope (chatter: alleen zichzelf)
 *   - diensten wijzigen:   owner/admin/manager, of head chatter voor zijn team
 *   - remove-chatter,
 *     reeks beëindigen,
 *     berichten en blokkeren: alleen owner/admin/manager
 *   - 2-11-2026 (Jordy): een head chatter blokkeert zijn eigen team vanaf de
 *     Roster (Chatters.tsx, met BlokkeerDialoog hieronder); deblokkeren niet
 * Verbergen is geen beveiliging; de server weigert het ook. Komt er toch een
 * 403, dan meldt run() die netjes.
 */
import { useMemo, useState } from 'react'
import { Avatar, ErrorNote, Loading, Panel, Seg, Table, Tag } from './ui'
import { Field, Modal } from './Modal'
import { useApi, usePoll } from '../lib/useApi'
import { useActions, mut } from '../lib/actions'
import { api } from '../lib/api'
import { isBeheer, isManager, useMij } from '../lib/rechten'
import type { Mij } from '../lib/rechten'
import { AMSTERDAM, alsZone, nlDagLabel, zoneDelen, zoneNaarUtc } from '../lib/amsterdam'

// ── Gegevensvormen (zie de backend voor de volledige velden) ─────────────

interface Dienst {
  id: number
  chatter_id: number
  chatter_name: string
  start_at: string
  end_at: string
  note: string | null
  duration_minutes: number
  series_id: number | null
  attendance: 'scheduled' | 'completed' | 'online' | 'away' | 'offline' | string
  alert_active: boolean
  last_seen_at: string | null
  is_online: boolean
  logout_kind: 'auto' | 'manual' | null
  shift_minutes: number
  elapsed_minutes: number
  online_during_shift_minutes: number
  coverage_pct: number | null
  low_coverage_warning: boolean
  coverage_warn_pct: number
}

interface DienstenAntwoord {
  access_scope: 'owner' | 'head_chatter' | 'self' | 'all_data' | string
  role: string
  chatters: { id: number; name: string }[]
  shifts: Dienst[]
  shift_coverage_warn_pct: number
}

interface Reeks {
  id: number
  chatter_id: number
  weekdays: number[]
  start_time_local: string
  duration_minutes: number
  note: string | null
  horizon_weeks: number
}

interface Bericht {
  id: number
  admin_user_id: number
  message: string
  created_at: string | null
  created_by: string
  acknowledged_at: string | null
  read: boolean
  telegram_sent: boolean
  is_block: boolean
  display_name?: string | null
  username?: string | null
}

interface Account {
  admin_user_id: number
  chatter_id: number | null
  username: string
  display_name: string
  role: string
  blocked: boolean
  blocked_by: string | null
  blocked_reason: string | null
  pending_notices: number
  is_online: boolean
  last_notice: Bericht | null
}

interface TelegramConfig {
  warnings: { id: string; severity: string; title: string; detail: string }[]
}

// ── Kleine helpers ───────────────────────────────────────────────────────

/** Weekdagnamen in de volgorde van Python weekday(): 0 = maandag. */
const WEEKDAGEN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/** Acht uur, zoals de backend (DEFAULT_SHIFT_MINUTES = 480). */
const STANDAARD_DUUR = 480
const DUREN = [240, 300, 360, 420, 480, 540, 600, 720]

function duurLabel(min: number): string {
  const h = Math.floor(min / 60)
  const m = Math.round(min % 60)
  return m ? `${h}h ${m}m` : `${h}h`
}

/** "2026-10-26" plus n dagen, zonder tijdzone (rekenen op de kalenderdag). */
function plusDagen(datum: string, n: number): string {
  const [j, m, d] = datum.split('-').map(Number)
  const x = new Date(Date.UTC(j, m - 1, d + n))
  return x.toISOString().slice(0, 10)
}

/** Weekdag van een kalenderdag, maandag = 0 (Python weekday()). */
function weekdagVan(datum: string): number {
  const [j, m, d] = datum.split('-').map(Number)
  return (new Date(Date.UTC(j, m - 1, d)).getUTCDay() + 6) % 7
}

/** Vandaag in agency-tijd. */
function vandaag(): string {
  return zoneDelen(new Date().toISOString(), AMSTERDAM)?.datum ?? new Date().toISOString().slice(0, 10)
}

/** "2026-10-26" -> "Mon 26-10", kort genoeg voor zeven kolommen. */
function dagKort(datum: string): string {
  const [, m, d] = datum.split('-')
  return `${WEEKDAGEN[weekdagVan(datum)]} ${d}-${m}`
}

function maandagVan(datum: string): string {
  return plusDagen(datum, -weekdagVan(datum))
}

/**
 * Een tijdstempel van de server als UTC lezen. De notices-tabel bewaart
 * `DateTime(timezone=True)`; op SQLite komt die zonder offset terug, en een
 * browser leest zo'n tekst als LOKALE tijd. Zonder offset plakken we er Z aan.
 */
function alsUtc(iso: string | null | undefined): string | null {
  if (!iso) return null
  return /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`
}

function tijdAms(iso: string | null | undefined): string {
  return zoneDelen(alsUtc(iso), AMSTERDAM)?.tijd ?? '—'
}

/** Einde op de wandklok, zoals de server het rekent (start lokaal + duur). */
function eindeWandklok(datum: string, tijd: string, duur: number): string {
  const [j, m, d] = datum.split('-').map(Number)
  const [u, mi] = tijd.split(':').map(Number)
  if (![j, m, d, u, mi].every(Number.isFinite)) return '—'
  const x = new Date(Date.UTC(j, m - 1, d, u, mi) + duur * 60_000)
  const p = (n: number) => String(n).padStart(2, '0')
  const day = x.toISOString().slice(0, 10)
  return `${day === datum ? '' : `${nlDagLabel(day)} `}${p(x.getUTCHours())}:${p(x.getUTCMinutes())}`
}

function naamVan(a: Pick<Account, 'display_name' | 'username'>): string {
  return a.display_name || a.username
}

/**
 * Mag ik dit account een bericht sturen of blokkeren? Spiegel van
 * `_assert_may_act_on`: nooit jezelf, nooit een owner, en alleen een owner
 * mag een manager aanpakken.
 */
function magAccountAanpakken(mij: Mij | null, a: Account): boolean {
  if (!mij) return false
  if (Number(a.admin_user_id) === Number(mij.id)) return false
  const doel = String(a.role ?? '').toLowerCase()
  if (doel === 'owner') return false
  const ik = String(mij.role ?? '').toLowerCase()
  if (ik !== 'owner' && doel !== 'chatter' && doel !== 'viewer') return false
  return true
}

/** Review-markering: dekking onder de drempel bij een lopende of afgelopen dienst. */
function heeftReview(s: Dienst, drempel: number): boolean {
  if (s.low_coverage_warning) return true
  return s.attendance !== 'scheduled'
    && Number(s.elapsed_minutes ?? 0) >= 30
    && s.coverage_pct != null
    && Number(s.coverage_pct) < drempel
}

// ── Het component ────────────────────────────────────────────────────────

export function ShiftAgenda() {
  const mij = useMij()
  const beheer = isBeheer(mij.data)
  const { run } = useActions()

  const [week, setWeek] = useState(() => maandagVan(vandaag()))
  const venster = useMemo(() => {
    const van = zoneNaarUtc(week, '00:00', AMSTERDAM) ?? new Date().toISOString()
    const tot = zoneNaarUtc(plusDagen(week, 7), '00:00', AMSTERDAM) ?? new Date().toISOString()
    return `from_at=${encodeURIComponent(van)}&to_at=${encodeURIComponent(tot)}`
  }, [week])

  const diensten = useApi<DienstenAntwoord>(`/api/admin/chatter-finance/shifts?${venster}`, [venster])
  const reeksen = useApi<{ series: Reeks[] }>('/api/admin/chatter-finance/shift-series')
  // Deze drie zijn require_manager_or_above: voor een ander geven ze 403, dus
  // we vragen ze dan niet eens.
  const accounts = useApi<{ accounts: Account[] }>(beheer ? '/api/admin/chatter-notices/accounts' : null)
  const telegram = useApi<TelegramConfig>(beheer ? '/api/admin/chatter-notices/telegram-config' : null)

  // De status van een lopende dienst verandert met de minuut; elke minuut is
  // genoeg (oud ververste alleen bij een klik).
  usePoll(60_000, () => {
    diensten.refetch()
    if (beheer) accounts.refetch()
  })

  const scope = diensten.data?.access_scope
  const magWijzigen = scope === 'owner' || scope === 'head_chatter'
  const drempel = Number(diensten.data?.shift_coverage_warn_pct ?? 80)
  const chatters = diensten.data?.chatters ?? []

  /** Account per chatters.id, zodat een dienst zijn login vindt. */
  const accountVanChatter = useMemo(() => {
    const m = new Map<number, Account>()
    for (const a of accounts.data?.accounts ?? []) {
      if (a.chatter_id) m.set(Number(a.chatter_id), a)
    }
    return m
  }, [accounts.data])

  const aanpakbaar = useMemo(
    () => (accounts.data?.accounts ?? [])
      .filter((a) => magAccountAanpakken(mij.data, a))
      .sort((a, b) => naamVan(a).localeCompare(naamVan(b))),
    [accounts.data, mij.data],
  )

  // Dialogen.
  const [dienstDialoog, setDienstDialoog] = useState<
    { soort: 'nieuw'; datum: string } | { soort: 'bewerk'; dienst: Dienst } | null
  >(null)
  const [berichtAan, setBerichtAan] = useState<Account | null>(null)
  const [blokkeer, setBlokkeer] = useState<Account | null>(null)
  const [historieFilter, setHistorieFilter] = useState<number | 'all'>('all')
  const [verwijderUitslag, setVerwijderUitslag] = useState<string | null>(null)

  const historie = useApi<{ notices: Bericht[] }>(
    beheer
      ? `/api/admin/chatter-notices/history?limit=80${
        historieFilter === 'all' ? '' : `&admin_user_id=${historieFilter}`}`
      : null,
    [historieFilter],
  )

  const naChatterWijziging = () => {
    accounts.refetch()
    historie.refetch()
    diensten.refetch()
  }

  const naDienstWijziging = () => {
    diensten.refetch()
    reeksen.refetch()
  }

  const deblokkeer = (a: Account) => run({
    label: `Unblock ${naamVan(a)}`,
    request: mut.post('/api/admin/chatter-notices/unblock', { admin_user_id: a.admin_user_id }),
    onDone: naChatterWijziging,
    successText: `${naamVan(a)} is unblocked.`,
  })

  const verwijderDienst = (s: Dienst, onDone?: () => void) => run({
    label: 'Delete shift',
    confirm: `Delete the shift of ${s.chatter_name} on ${alsZone(s.start_at, AMSTERDAM)}?`,
    request: mut.del(`/api/admin/chatter-finance/shifts/${s.id}`),
    onDone: () => { naDienstWijziging(); onDone?.() },
    successText: 'Shift deleted.',
  })

  const haalVanRooster = (c: { id: number; name: string }) => {
    const herhaalt = (reeksen.data?.series ?? []).some((r) => r.chatter_id === c.id)
    setVerwijderUitslag(null)
    return run({
      label: `Remove ${c.name} from schedule`,
      confirm: `Remove ${c.name} from the schedule?\n\nAll FUTURE shifts are deleted`
        + (herhaalt ? ' and every repeating pattern is stopped' : '')
        + '. Past and running shifts stay for attendance history.',
      // Het aantal komt uit het antwoord; run() kent alleen een vaste tekst,
      // dus de uitslag zetten we hier zelf neer.
      request: async () => {
        const r = await api.post<{ removed_shifts: number; ended_series: number }>(
          '/api/admin/chatter-finance/shifts/remove-chatter', { chatter_id: c.id })
        const weg = Number(r?.removed_shifts ?? 0)
        const gestopt = Number(r?.ended_series ?? 0)
        setVerwijderUitslag(weg === 0 && gestopt === 0
          ? `${c.name}: no future shifts and no repeating pattern left. Past shifts stay on the agenda for attendance history.`
          : `${c.name}: removed ${weg} future shift${weg === 1 ? '' : 's'}`
            + (gestopt ? ` and stopped ${gestopt} repeating pattern${gestopt === 1 ? '' : 's'}` : '')
            + '. Past shifts stay for history.')
      },
      onDone: naDienstWijziging,
      successText: `${c.name} removed from the schedule.`,
    })
  }

  const dagen = Array.from({ length: 7 }, (_, i) => plusDagen(week, i))
  const vandaagDatum = vandaag()
  const perDag = useMemo(() => {
    const m = new Map<string, Dienst[]>()
    for (const s of diensten.data?.shifts ?? []) {
      const dag = zoneDelen(s.start_at, AMSTERDAM)?.datum
      if (!dag) continue
      const lijst = m.get(dag) ?? []
      lijst.push(s)
      m.set(dag, lijst)
    }
    return m
  }, [diensten.data])

  const deezeWeek = maandagVan(vandaagDatum)

  return (
    <>
      {/* e) Zonder chatterbot-token blijft /start in Telegram eeuwig
          onbeantwoord en zegt niets waarom; oud zette dit bovenaan. */}
      {beheer && (telegram.data?.warnings ?? []).length ? (
        <div className="sa-waarschuwingen">
          {(telegram.data?.warnings ?? []).map((w) => (
            <div key={w.id} className="note sa-waarschuwing" role="alert">
              <b>⚠ {w.title}</b>
              <div>{w.detail}</div>
            </div>
          ))}
        </div>
      ) : null}

      {/* c) De weekkalender. */}
      <Panel
        title="Shift agenda"
        desc={`Week of ${nlDagLabel(week)} – ${nlDagLabel(plusDagen(week, 6))} · times in Amsterdam`}
        right={
          <>
            <button className="btn ghost sm" type="button" onClick={() => setWeek(plusDagen(week, -7))}>
              ‹ Previous
            </button>
            <button className="btn ghost sm" type="button" disabled={week === deezeWeek}
                    onClick={() => setWeek(deezeWeek)}>
              This week
            </button>
            <button className="btn ghost sm" type="button" onClick={() => setWeek(plusDagen(week, 7))}>
              Next ›
            </button>
            {magWijzigen ? (
              <button className="btn primary sm" type="button"
                      onClick={() => setDienstDialoog({
                        soort: 'nieuw',
                        datum: week === deezeWeek ? vandaagDatum : week,
                      })}>
                Add shift
              </button>
            ) : scope === 'self' ? <Tag kind="acc">My shifts</Tag> : null}
          </>
        }
      >
        {diensten.loading ? <Loading /> : diensten.error ? <ErrorNote error={diensten.error} /> : (
          <div className="sa-week">
            {dagen.map((dag) => {
              const lijst = perDag.get(dag) ?? []
              return (
                <div key={dag} className={dag === vandaagDatum ? 'sa-day today' : 'sa-day'}>
                  <div className="sa-daykop">
                    <span>{dagKort(dag)}</span>
                    {magWijzigen ? (
                      <button className="btn ghost sm" type="button" title={`Add a shift on ${nlDagLabel(dag)}`}
                              aria-label={`Add a shift on ${nlDagLabel(dag)}`}
                              onClick={() => setDienstDialoog({ soort: 'nieuw', datum: dag })}>
                        +
                      </button>
                    ) : null}
                  </div>
                  <div className="sa-daylist">
                    {lijst.length === 0 ? <div className="dim sa-leeg">No shifts</div> : lijst.map((s) => (
                      <DienstBlok
                        key={s.id}
                        dienst={s}
                        drempel={drempel}
                        account={beheer ? accountVanChatter.get(Number(s.chatter_id)) : undefined}
                        magWijzigen={magWijzigen}
                        onBewerk={() => setDienstDialoog({ soort: 'bewerk', dienst: s })}
                        onVerwijder={() => verwijderDienst(s)}
                        onBericht={(a) => setBerichtAan(a)}
                      />
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Panel>

      {/* b) Remove from schedule: "a button to delete one chatter from the list
          in case we stop with a chatter and have a schedule for 12 weeks still
          open for him". Alleen beheer (require_manager_or_above). */}
      {beheer && magWijzigen && chatters.length ? (
        <Panel title="Remove from schedule"
               desc="Deletes a chatter's future shifts and stops their repeating patterns. Past shifts stay for attendance history.">
          <div className="sa-chips">
            {chatters.map((c) => {
              const herhaalt = (reeksen.data?.series ?? []).some((r) => r.chatter_id === c.id)
              return (
                <span key={c.id} className="sa-chip">
                  <Avatar name={c.name} />
                  {c.name}{herhaalt ? <span className="mut2" title="Has a repeating pattern"> ↻</span> : null}
                  <button className="btn ghost sm" type="button"
                          title={`Remove ${c.name} from the schedule`}
                          aria-label={`Remove ${c.name} from the schedule`}
                          onClick={() => haalVanRooster(c)}>
                    ✕
                  </button>
                </span>
              )
            })}
          </div>
          {verwijderUitslag ? (
            <div className="note" style={{ marginTop: 10 }}>
              {verwijderUitslag}{' '}
              <button className="btn ghost sm" type="button" onClick={() => setVerwijderUitslag(null)}>Dismiss</button>
            </div>
          ) : null}
        </Panel>
      ) : null}

      {/* b) Reeksen: wat blijft herhalen, met weekdagNAMEN in plaats van 0-6. */}
      <Panel title="Repeating shifts" desc="Patterns that keep topping up the agenda until they are ended" flush>
        {reeksen.loading ? <Loading /> : reeksen.error ? <ErrorNote error={reeksen.error} /> : (
          <Table
            cols={[
              { key: 'c', label: 'Chatter' }, { key: 'w', label: 'Days' },
              { key: 's', label: 'Start (Amsterdam)' }, { key: 'd', label: 'Length', num: true },
              { key: 'h', label: 'Horizon', num: true }, { key: 'n', label: 'Note' },
              ...(beheer ? [{ key: 'x', label: '' }] : []),
            ]}
            emptyText="No repeating shifts."
          >
            {(reeksen.data?.series ?? []).map((r) => {
              const naam = chatters.find((c) => c.id === r.chatter_id)?.name ?? `Chatter #${r.chatter_id}`
              const dagenTekst = r.weekdays.length === 7
                ? 'Every day'
                : r.weekdays.map((d) => WEEKDAGEN[d] ?? String(d)).join(', ')
              return (
                <tr key={r.id}>
                  <td><b><Avatar name={naam} />{naam}</b></td>
                  <td>{dagenTekst || '—'}</td>
                  <td className="mono">{r.start_time_local}</td>
                  <td className="num mono">{duurLabel(Number(r.duration_minutes || STANDAARD_DUUR))}</td>
                  <td className="num mono">{r.horizon_weeks} wk</td>
                  <td className={r.note ? 'mut' : 'dim'}>{r.note || '—'}</td>
                  {beheer ? (
                    <td>
                      <button className="btn ghost sm" type="button"
                              onClick={() => run({
                                label: 'End repeating shift',
                                confirm: `Stop the repeating pattern of ${naam} (${dagenTekst} ${r.start_time_local})?\n\n`
                                  + 'Shifts already on the agenda stay; you can still edit or delete them one by one.',
                                request: mut.post('/api/admin/chatter-finance/shift-series/end', { series_id: r.id }),
                                onDone: naDienstWijziging,
                                successText: 'Repeating pattern ended.',
                              })}>
                        End series
                      </button>
                    </td>
                  ) : null}
                </tr>
              )
            })}
          </Table>
        )}
      </Panel>

      {/* a) Message or block. Oud: "the per-shift controls are useless on a
          week with no shifts booked", dus deze lijst staat er altijd. */}
      {beheer ? (
        <Panel
          title="Message or block a chatter"
          desc={<>
            The message takes over their whole screen and only goes away once they confirm it with
            “I have read this”. It only appears on <b>this</b> agency's dashboard — message them where they are
            working right now. Blocking locks their dashboard immediately (red screen, every request refused) until you unblock them from the same row.
          </>}
          flush
        >
          {accounts.loading ? <Loading /> : accounts.error ? <ErrorNote error={accounts.error} /> : (
            <Table
              cols={[
                { key: 'n', label: 'Chatter' }, { key: 's', label: 'Status' },
                { key: 'l', label: 'Latest message' }, { key: 'x', label: '' },
              ]}
              emptyText="No chatter logins you can message on this agency."
            >
              {aanpakbaar.map((a) => (
                <tr key={a.admin_user_id}>
                  <td>
                    <b><Avatar name={naamVan(a)} />{naamVan(a)}</b>
                    {String(a.role).toLowerCase() !== 'chatter'
                      ? <> <Tag kind="mut">{a.role}</Tag></> : null}
                  </td>
                  <td><AccountStatus account={a} /></td>
                  <td className={a.last_notice ? 'mut' : 'dim'} style={{ maxWidth: 320 }}>
                    {a.last_notice ? (
                      <span className="sa-afkap" title={a.last_notice.message}>
                        {alsZone(alsUtc(a.last_notice.created_at), AMSTERDAM)} · {a.last_notice.message}
                      </span>
                    ) : '—'}
                  </td>
                  <td>
                    <div className="row" style={{ gap: 6, justifyContent: 'flex-end' }}>
                      <button className="btn ghost sm" type="button" onClick={() => setBerichtAan(a)}>
                        Message
                      </button>
                      {/* 2-11-2026 (Jordy/architect): een manager blokkeert alleen als
                          head, vanaf de Roster (eigen team); hier heropent hij alleen. */}
                      {a.blocked ? (
                        <button className="btn sm" type="button" onClick={() => deblokkeer(a)}>Unblock</button>
                      ) : !isManager(mij.data) ? (
                        <button className="btn danger sm" type="button" onClick={() => setBlokkeer(a)}>Block</button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      ) : null}

      {/* d) Berichtgeschiedenis met leesbevestiging. */}
      {beheer ? (
        <Panel
          title="Message history"
          desc="Read = they pressed “I have read this” on the full-screen box. Waiting = still open on their screen, or they have not signed in since."
          right={
            <>
              <select className="select sm" value={String(historieFilter)}
                      onChange={(e) => setHistorieFilter(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
                <option value="all">All chatters</option>
                {aanpakbaar.map((a) => (
                  <option key={a.admin_user_id} value={String(a.admin_user_id)}>{naamVan(a)}</option>
                ))}
              </select>
              <button className="btn ghost sm" type="button" disabled={historie.loading || accounts.loading}
                      onClick={() => { accounts.refetch(); historie.refetch() }}>
                Refresh status
              </button>
            </>
          }
          flush
        >
          {historie.loading ? <Loading /> : historie.error ? <ErrorNote error={historie.error} /> : (
            <Table
              cols={[
                { key: 't', label: 'Sent' }, { key: 'c', label: 'Chatter' },
                { key: 'm', label: 'Message' }, { key: 'b', label: 'Sent by' },
                { key: 's', label: 'Status' }, { key: 'tg', label: 'Telegram' },
              ]}
              emptyText="No messages sent yet on this agency."
            >
              {(historie.data?.notices ?? []).map((n) => (
                <tr key={n.id}>
                  <td className="mut2" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                    {alsZone(alsUtc(n.created_at), AMSTERDAM)}
                  </td>
                  <td><b>{n.display_name || n.username || `User #${n.admin_user_id}`}</b></td>
                  <td className="sa-bericht">
                    {n.is_block ? <><Tag kind="bad">Block</Tag> </> : null}{n.message}
                  </td>
                  <td className={n.created_by ? 'mut' : 'dim'}>{n.created_by || '—'}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {n.read
                      ? <Tag kind="ok" title="They pressed “I have read this”">
                          Read{n.acknowledged_at ? ` · ${alsZone(alsUtc(n.acknowledged_at), AMSTERDAM)}` : ''}
                        </Tag>
                      : <Tag kind="warn">Waiting</Tag>}
                  </td>
                  <td>{n.telegram_sent ? <Tag kind="ok">Yes</Tag> : <Tag kind="mut">No</Tag>}</td>
                </tr>
              ))}
            </Table>
          )}
        </Panel>
      ) : null}

      {dienstDialoog ? (
        <DienstDialoog
          key={dienstDialoog.soort === 'bewerk' ? `b${dienstDialoog.dienst.id}` : `n${dienstDialoog.datum}`}
          stand={dienstDialoog}
          chatters={chatters}
          onClose={() => setDienstDialoog(null)}
          onOpgeslagen={() => { setDienstDialoog(null); naDienstWijziging() }}
          onVerwijder={(s) => verwijderDienst(s, () => setDienstDialoog(null))}
        />
      ) : null}

      {berichtAan ? (
        <BerichtDialoog
          account={berichtAan}
          onClose={() => setBerichtAan(null)}
          onVerstuurd={() => {
            setHistorieFilter(berichtAan.admin_user_id)
            setBerichtAan(null)
            naChatterWijziging()
          }}
        />
      ) : null}

      {blokkeer ? (
        <BlokkeerDialoog
          account={blokkeer}
          onClose={() => setBlokkeer(null)}
          onGeblokkeerd={() => { setBlokkeer(null); naChatterWijziging() }}
        />
      ) : null}
    </>
  )
}

// ── Subcomponenten ───────────────────────────────────────────────────────

/** De statuschips van een login: Blocked / Online here / Waiting / Read. */
function AccountStatus({ account: a }: { account: Account }) {
  const wachtend = Number(a.pending_notices ?? 0)
  const laatst = a.last_notice
  return (
    <span className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
      {a.blocked ? (
        <Tag kind="bad" title={a.blocked_reason ?? undefined}>
          {a.blocked_by ? `Blocked by ${a.blocked_by}` : 'Blocked'}
        </Tag>
      ) : null}
      {a.is_online && !a.blocked ? (
        <Tag kind="ok" title="Signed into this dashboard right now — a message reaches their screen within seconds">
          <span className="dotst on-live" />Online here
        </Tag>
      ) : null}
      {!a.blocked && wachtend > 0 ? (
        <Tag kind="warn" title={laatst?.message ? `Latest: ${laatst.message}` : 'Not confirmed yet'}>
          {wachtend === 1 ? 'Waiting for read' : `${wachtend} waiting`}
        </Tag>
      ) : null}
      {!a.blocked && wachtend === 0 && laatst?.read ? (
        <Tag kind="mut" title={laatst.acknowledged_at
          ? `Confirmed ${alsZone(alsUtc(laatst.acknowledged_at), AMSTERDAM)}` : undefined}>
          Read
        </Tag>
      ) : null}
      {!a.blocked && !a.is_online && wachtend === 0 && !laatst?.read ? <span className="dim">Offline</span> : null}
    </span>
  )
}

/** Eén dienst in de weekkalender. */
function DienstBlok({
  dienst: s, drempel, account, magWijzigen, onBewerk, onVerwijder, onBericht,
}: {
  dienst: Dienst
  drempel: number
  account?: Account
  magWijzigen: boolean
  onBewerk: () => void
  onVerwijder: () => void
  onBericht: (a: Account) => void
}) {
  const review = heeftReview(s, drempel)
  const geblokkeerd = !!account?.blocked
  const klas = ['sa-blok', review ? 'review' : '', s.alert_active || geblokkeerd ? 'alarm' : '']
    .filter(Boolean).join(' ')
  return (
    <div className={klas}>
      <div className="sa-blokkop">
        <b className="sa-afkap"><Avatar name={s.chatter_name} size={18} />{s.chatter_name}</b>
        {magWijzigen ? (
          <span className="sa-knoppen">
            {account ? (
              <button className="btn ghost sm" type="button" title={`Message ${s.chatter_name}`}
                      aria-label={`Message ${s.chatter_name}`} onClick={() => onBericht(account)}>✉</button>
            ) : null}
            <button className="btn ghost sm" type="button" title="Edit shift" aria-label="Edit shift"
                    onClick={onBewerk}>✎</button>
            <button className="btn ghost sm" type="button" title="Delete shift" aria-label="Delete shift"
                    onClick={onVerwijder}>🗑</button>
          </span>
        ) : null}
      </div>
      <div className="mut2 sa-tijd">
        {tijdAms(s.start_at)}–{tijdAms(s.end_at)} · {duurLabel(Number(s.duration_minutes || STANDAARD_DUUR))}
        {s.series_id ? <span title="Part of a repeating pattern"> ↻</span> : null}
      </div>
      <div className="row" style={{ gap: 4, flexWrap: 'wrap', marginTop: 4 }}>
        <StatusTag dienst={s} geblokkeerd={geblokkeerd} blocker={account?.blocked_by ?? null} />
        {review ? (
          <Tag kind="warn"
               title={`Online ${s.online_during_shift_minutes ?? 0} of ${s.elapsed_minutes || s.shift_minutes || 0} min `
                 + `(under ${drempel}%). Review this shift.`}>
            Review · {Number(s.coverage_pct ?? 0)}% online
          </Tag>
        ) : null}
      </div>
      {s.note ? <div className="mut sa-notitie">{s.note}</div> : null}
    </div>
  )
}

function StatusTag({ dienst: s, geblokkeerd, blocker }: {
  dienst: Dienst; geblokkeerd: boolean; blocker: string | null
}) {
  if (geblokkeerd) return <Tag kind="bad">{blocker ? `Blocked by ${blocker}` : 'Blocked'}</Tag>
  switch (s.attendance) {
    case 'completed': return <Tag kind="mut">Completed</Tag>
    case 'scheduled': return <Tag kind="acc">Scheduled</Tag>
    case 'online': return <Tag kind="ok"><span className="dotst on-live" />Online</Tag>
    case 'away': return <Tag kind={s.alert_active ? 'bad' : 'warn'}>Away</Tag>
    default: {
      const extra = s.logout_kind === 'auto' ? ' · auto logoff' : s.logout_kind === 'manual' ? ' · logged out' : ''
      return (
        <Tag kind={s.alert_active ? 'bad' : 'mut'}
             title={s.last_seen_at ? `Last seen ${alsZone(alsUtc(s.last_seen_at), AMSTERDAM)}` : undefined}>
          Offline{extra}
        </Tag>
      )
    }
  }
}

/** Dienst toevoegen of bewerken. Bij bewerken geen herhaling: dat is één rij. */
function DienstDialoog({
  stand, chatters, onClose, onOpgeslagen, onVerwijder,
}: {
  stand: { soort: 'nieuw'; datum: string } | { soort: 'bewerk'; dienst: Dienst }
  chatters: { id: number; name: string }[]
  onClose: () => void
  onOpgeslagen: () => void
  onVerwijder: (s: Dienst) => void
}) {
  const { run, busy } = useActions()
  const bewerk = stand.soort === 'bewerk' ? stand.dienst : null
  const begin = bewerk ? zoneDelen(bewerk.start_at, AMSTERDAM) : null

  const [chatter, setChatter] = useState<number | ''>(bewerk?.chatter_id ?? chatters[0]?.id ?? '')
  const [datum, setDatum] = useState(begin?.datum ?? (stand.soort === 'nieuw' ? stand.datum : vandaag()))
  const [tijd, setTijd] = useState(begin?.tijd ?? '09:00')
  const [duur, setDuur] = useState<number>(Number(bewerk?.duration_minutes) || STANDAARD_DUUR)
  const [notitie, setNotitie] = useState(bewerk?.note ?? '')
  const [weekdagen, setWeekdagen] = useState<number[]>([])
  const [herhaal, setHerhaal] = useState<'weeks' | 'forever'>('weeks')
  const [weken, setWeken] = useState(1)

  const keuzes = DUREN.includes(duur) ? DUREN : [...DUREN, duur].sort((a, b) => a - b)
  const startIso = zoneNaarUtc(datum, tijd, AMSTERDAM)
  const label = bewerk ? 'Update shift' : 'Save shift'
  const geldig = chatter !== '' && !!startIso && notitie.length <= 500

  const wisselDag = (d: number) => setWeekdagen((w) =>
    w.includes(d) ? w.filter((x) => x !== d) : [...w, d].sort((a, b) => a - b))

  const opslaan = () => {
    if (!geldig || !startIso) return
    const basis = {
      chatter_id: Number(chatter),
      start_at: startIso,
      duration_minutes: duur,
      note: notitie.trim() || null,
    }
    void run({
      label,
      request: bewerk
        ? mut.patch(`/api/admin/chatter-finance/shifts/${bewerk.id}`, basis)
        : mut.post('/api/admin/chatter-finance/shifts', {
          ...basis,
          // "Voor altijd": de eerste twaalf weken staan meteen in de agenda,
          // daarna vult de reeks zichzelf aan tot hij beëindigd wordt.
          repeat_weeks: herhaal === 'forever' ? 12 : Math.max(1, Math.min(12, weken)),
          repeat_forever: herhaal === 'forever',
          // Niets aangevinkt = alleen de weekdag van de gekozen datum; dan
          // sturen we de lijst niet mee, zoals oud.
          ...(weekdagen.length ? { weekdays: weekdagen } : {}),
        }),
      onDone: onOpgeslagen,
      successText: bewerk ? 'Shift updated.' : 'Shift saved.',
    })
  }

  return (
    <Modal
      title={bewerk ? `Edit shift — ${bewerk.chatter_name}` : 'Add shift'}
      desc="Date and time are in Amsterdam time (the agency's clock), whatever your own time zone is."
      width={520}
      onClose={onClose}
      footer={
        <>
          {bewerk ? (
            <button className="btn danger sm" type="button" style={{ marginRight: 'auto' }}
                    onClick={() => onVerwijder(bewerk)}>
              Delete
            </button>
          ) : null}
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary sm" type="button" disabled={!geldig || busy === label} onClick={opslaan}>
            {busy === label ? 'Saving…' : label}
          </button>
        </>
      }
    >
      <Field label="Chatter">
        <select className="inp" value={String(chatter)}
                onChange={(e) => setChatter(e.target.value ? Number(e.target.value) : '')}>
          {chatters.length === 0 ? <option value="">No chatters in your scope</option> : null}
          {chatters.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <div className="cols" style={{ gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Date">
          <input className="inp" type="date" value={datum} onChange={(e) => setDatum(e.target.value)} />
        </Field>
        <Field label="Start time (Amsterdam)">
          <input className="inp" type="time" value={tijd} onChange={(e) => setTijd(e.target.value)} />
        </Field>
      </div>
      <Field label="Shift length"
             hint={`Ends ${startIso ? eindeWandklok(datum, tijd, duur) : '—'} (Amsterdam). Overnight shifts roll into the next day.`}>
        <select className="inp" value={duur} onChange={(e) => setDuur(Number(e.target.value) || STANDAARD_DUUR)}>
          {keuzes.map((d) => <option key={d} value={d}>{duurLabel(d)}</option>)}
        </select>
      </Field>

      {!bewerk ? (
        <>
          <Field label="Days" hint="Leave all unticked for just the weekday of the date above.">
            <div className="sa-days">
              {WEEKDAGEN.map((naam, i) => (
                <label key={naam} className="sa-daycheck">
                  <input type="checkbox" checked={weekdagen.includes(i)} onChange={() => wisselDag(i)} />
                  {naam}
                </label>
              ))}
              <button className="btn ghost sm" type="button"
                      onClick={() => setWeekdagen(weekdagen.length === 7 ? [] : [0, 1, 2, 3, 4, 5, 6])}>
                {weekdagen.length === 7 ? 'Clear' : 'All days'}
              </button>
            </div>
          </Field>
          <Field label="Repeat"
                 hint={herhaal === 'forever'
                   ? 'Fills the next 12 weeks now and keeps topping up the agenda until you end the series.'
                   : '1 = this week only; up to 12 creates the same shift every week.'}>
            <div className="row" style={{ gap: 10, flexWrap: 'wrap' }}>
              <Seg value={herhaal} onChange={(v) => setHerhaal(v as 'weeks' | 'forever')}
                   options={[{ value: 'weeks', label: 'Number of weeks' }, { value: 'forever', label: 'Forever' }]} />
              {herhaal === 'weeks' ? (
                <input className="inp" type="number" min={1} max={12} style={{ width: 90 }} value={weken}
                       onChange={(e) => setWeken(Math.max(1, Math.min(12, Number(e.target.value || 1))))} />
              ) : null}
            </div>
          </Field>
        </>
      ) : null}

      <Field label="Note (optional)" hint={notitie.length > 500 ? 'At most 500 characters.' : undefined}>
        <input className="inp" value={notitie} maxLength={500} onChange={(e) => setNotitie(e.target.value)} />
      </Field>
    </Modal>
  )
}

/** Schermvullend bericht naar één login. */
function BerichtDialoog({ account: a, onClose, onVerstuurd }: {
  account: Account; onClose: () => void; onVerstuurd: () => void
}) {
  const { run, busy } = useActions()
  const [tekst, setTekst] = useState('')
  const label = `Message ${naamVan(a)}`
  return (
    <Modal
      title={label}
      desc={<>
        The message appears <b>full-screen</b> on {naamVan(a)}'s dashboard and only goes away once they
        confirm it with “I have read this”. It reaches them within a few seconds if they are signed into
        this agency; a copy goes to Telegram if they linked it.
      </>}
      width={520}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button className="btn primary sm" type="button" disabled={!tekst.trim() || busy === label}
                  onClick={() => {
                    // De Telegram-uitkomst staat pas in het antwoord. run() leest
                    // successText na afloop van request, dus we vullen hem daar in.
                    const opdracht = {
                      label,
                      successText: `Message sent to ${naamVan(a)}.`,
                      request: async () => {
                        const r = await api.post<{ telegram_sent: boolean }>('/api/admin/chatter-notices/send',
                          { admin_user_id: a.admin_user_id, message: tekst.trim() })
                        opdracht.successText = r?.telegram_sent
                          ? `Sent to ${naamVan(a)}, with a copy on Telegram. Status stays Waiting until they confirm.`
                          : `Sent to ${naamVan(a)} on this dashboard (no Telegram linked). Status stays Waiting until they confirm.`
                      },
                      onDone: onVerstuurd,
                    }
                    void run(opdracht)
                  }}>
            {busy === label ? 'Sending…' : 'Send message'}
          </button>
        </>
      }
    >
      {a.blocked ? (
        <div className="note" style={{ marginBottom: 12 }}>
          This account is blocked; the message is queued behind the block notice.
        </div>
      ) : null}
      <Field label="Message" hint={`${tekst.length} / 2000`}>
        <textarea className="inp" rows={5} maxLength={2000} value={tekst} autoFocus
                  onChange={(e) => setTekst(e.target.value)} />
      </Field>
    </Modal>
  )
}

/** Wie geblokkeerd wordt: genoeg om de dialoog ook vanaf de Roster te openen. */
export type BlokDoel = Pick<Account, 'admin_user_id' | 'display_name' | 'username'>

/**
 * Blokkeren met reden; de bevestiging zegt wat er meteen gebeurt.
 *
 * 2-11-2026 (Jordy): ook voor een head chatter op de Roster (Chatters.tsx).
 * Met `redenVerplicht` is de reden verplicht, zoals de server dat voor een
 * head eist (400 zonder reden).
 */
export function BlokkeerDialoog({ account: a, onClose, onGeblokkeerd, redenVerplicht = false }: {
  account: BlokDoel; onClose: () => void; onGeblokkeerd: () => void; redenVerplicht?: boolean
}) {
  const { run, busy } = useActions()
  const [reden, setReden] = useState('')
  const label = `Block ${naamVan(a)}`
  return (
    <Modal
      title={label}
      desc="They see a red block notice on their screen and cannot continue until a manager unblocks them."
      width={480}
      onClose={onClose}
      footer={
        <>
          <button className="btn ghost sm" type="button" onClick={onClose}>Cancel</button>
          <button className="btn danger sm" type="button"
                  disabled={busy === label || (redenVerplicht && !reden.trim())}
                  onClick={() => run({
                    label,
                    confirm: redenVerplicht
                      ? `Block ${naamVan(a)}? This locks their screen immediately (red screen) until a manager unblocks them.`
                      : `Block ${naamVan(a)}? Their dashboard locks immediately (red screen) until someone unblocks them.`,
                    request: mut.post('/api/admin/chatter-notices/block',
                      { admin_user_id: a.admin_user_id, reason: reden.trim() || null }),
                    onDone: onGeblokkeerd,
                    successText: `${naamVan(a)} is blocked.`,
                  })}>
            {busy === label ? 'Blocking…' : 'Block'}
          </button>
        </>
      }
    >
      <Field label="Reason (shown to them)"
             hint={redenVerplicht
               ? 'Required. Only a manager can unblock them again.'
               : 'Optional. Without a reason they are told to contact a manager on Telegram.'}>
        <textarea className="inp" rows={3} maxLength={500} value={reden} onChange={(e) => setReden(e.target.value)} />
      </Field>
    </Modal>
  )
}
